<script setup lang="ts">
import { computed, CSSProperties, ref } from 'vue';
import BtnIcon from '../BtnIcon/BtnIcon.vue';

const emit = defineEmits<{
	(e: 'closeModal'): void;
}>();

const props = defineProps<{
	showModal: boolean;
	title: string;
	size?: 'small' | 'large' | 'normal';
	minimumWidth?: number;
	minimumHeight?: number;
}>();

const modal = ref(null);

const closeModal = () => {
	emit('closeModal');
};

const computedSizeClass = computed(() => {
	switch (props.size) {
		case 'small':
			return 'modal-backdrop__modal--small';
		case 'large':
			return 'modal-backdrop__modal--large';
		case 'normal':
		default:
			return '';
	}
});
const computedMinWidth = (): CSSProperties => {
	if (props.minimumWidth) {
		return {
			'min-width': `${props.minimumWidth}px`,
		};
	}
	return {};
};
const computedMinHeight = (): CSSProperties => {
	if (props.minimumHeight) {
		return {
			'min-height': `${props.minimumHeight}px`,
		};
	}
	return {};
};
</script>
<template>
	<teleport to="body">
		<transition
			appear
			enter-active-class="enter-active"
			enter-from-class="enter-from"
			enter-to-class="enter-to"
			leave-active-class="leave-active"
			leave-from-class="leave-from"
			leave-to-class="leave-to"
		>
			<div
				v-if="showModal"
				data-ts="modal-backdrop"
				class="modal-backdrop"
				@click.self="closeModal"
			>
				<transition
					appear
					enter-active-class="enter-active-modal"
					enter-from-class="enter-from-modal"
					enter-to-class="enter-to-modal"
					leave-active-class="leave-active-modal"
					leave-from-class="leave-from-modal"
					leave-to-class="leave-to-modal"
				>
					<div
						v-show="showModal"
						:class="['modal-backdrop__modal relative', computedSizeClass]"
						:style="[computedMinWidth(), computedMinHeight()]"
						role="dialog"
						ref="modal"
						aria-modal="true"
						aria-labelledby="modal"
					>
						<div class="modal-backdrop__modal__header">
							<span class="heading" data-ts="heading">{{ title }}</span>
							<btn-icon
								icon-name="bx bx-x"
								ariaLabel="close modal"
								data-ts="close-btn"
								@click="closeModal"
							/>
						</div>
						<div class="modal-backdrop__modal__body">
							<slot v-if="$slots.body" name="body">modal body</slot>
						</div>
						<div class="modal-backdrop__modal__footer">
							<slot v-if="$slots.footer" name="footer">modal footer</slot>
						</div>
					</div>
				</transition>
			</div>
		</transition>
	</teleport>
</template>

<style lang="scss" scoped>
.modal-backdrop {
	position: fixed;
	width: 100vw;
	height: 100vh;
	top: 0;
	left: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 2% 0;
	background-color: rgba(10, 10, 10, 0.7);
	z-index: $z-index-3;

	&__modal {
		background-color: white;
		height: fit-content;
		max-height: 80vh;
		width: 50%;
		min-width: 500px;
		border-radius: $border-radius-2;
		padding: $spacing-4;
		display: flex;
		flex-direction: column;
		overflow: auto;

		&--small {
			width: 35%;
		}

		&--large {
			width: 60%;
		}

		&__header,
		&__body,
		&__footer {
			padding: $spacing-2;
		}

		&__header {
			display: flex;
			align-items: center;
			justify-content: space-between;
		}

		&__body {
			flex: 2;
			overflow-y: scroll;
		}
	}
}

.enter-active {
	transition: opacity 200ms ease-out;
}

.leave-active {
	transition: opacity 200ms ease-in;
}

.enter-from,
.leave-to {
	opacity: 0;
}

.enter-to,
.leave-from {
	opacity: 1;
}

.enter-active-modal {
	transition: all 300ms ease-out;
}

.leave-active-modal {
	transition: all 300ms ease-in;
}

.enter-from-modal,
.leave-to-modal {
	opacity: 0;
	transform: translateY(30px) scale(0.95);
}

.enter-to-modal,
.leave-from-modal {
	opacity: 1;
	transform: translateY(0) scale(1);
}
</style>
